import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';

// Context
import AuthContext from '../../context/AuthContext';

// Services
import {
  getTechnicianInventorySummary,
  updateTechnicianInventoryStatus,
  returnInventoryFromTechnician,
  getStatusDisplayText,
  getStatusColor,
  getAvailableStatusTransitions,
  canReturnInventory,
  formatDate
} from '../../services/technicianInventoryService';

const TechnicianInventorySummary = () => {
  const { user } = useContext(AuthContext);
  const { technicianId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [returnQuantity, setReturnQuantity] = useState('');
  const [returnNotes, setReturnNotes] = useState('');

  // Check if user can manage inventory
  const canManage = user && ['manager', 'purchaser', 'warehouse_staff'].includes(user.role);

  // Fetch technician inventory summary
  const fetchData = async () => {
    try {
      setLoading(true);
      const summaryData = await getTechnicianInventorySummary(technicianId);
      setData(summaryData);
      setError(null);
    } catch (err) {
      console.error('Error fetching technician inventory summary:', err);
      setError('Failed to load technician inventory summary. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (technicianId) {
      fetchData();
    }
  }, [technicianId]);

  // Handle status update
  const handleOpenStatusDialog = (record) => {
    setSelectedRecord(record);
    setNewStatus('');
    setStatusNotes('');
    setStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setSelectedRecord(null);
    setNewStatus('');
    setStatusNotes('');
  };

  const handleStatusUpdate = async () => {
    try {
      await updateTechnicianInventoryStatus(selectedRecord.id, {
        status: newStatus,
        notes: statusNotes
      });

      handleCloseStatusDialog();
      fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.message || 'Failed to update status');
    }
  };

  // Handle return
  const handleOpenReturnDialog = (record) => {
    setSelectedRecord(record);
    setReturnQuantity(record.quantity.toString());
    setReturnNotes('');
    setReturnDialogOpen(true);
  };

  const handleCloseReturnDialog = () => {
    setReturnDialogOpen(false);
    setSelectedRecord(null);
    setReturnQuantity('');
    setReturnNotes('');
  };

  const handleReturn = async () => {
    try {
      await returnInventoryFromTechnician(selectedRecord.id, {
        quantity: parseInt(returnQuantity),
        notes: returnNotes
      });

      handleCloseReturnDialog();
      fetchData();
    } catch (err) {
      console.error('Error returning inventory:', err);
      setError(err.message || 'Failed to return inventory');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          <AlertTitle>No Data</AlertTitle>
          No technician data found.
        </Alert>
      </Container>
    );
  }

  const { technician, current_inventory, status_summary } = data;

  // Convert status summary to object for easier access
  const statusCounts = status_summary.reduce((acc, item) => {
    acc[item.status] = {
      count: parseInt(item.count),
      total_quantity: parseInt(item.total_quantity)
    };
    return acc;
  }, {});

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/technicians')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1">
              {technician.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {technician.type} - Vehicle Inventory
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/technician-inventory')}
            startIcon={<InventoryIcon />}
          >
            Manage All Inventory
          </Button>
        </Box>
      </Box>

      {/* Status Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Assigned
              </Typography>
              <Typography variant="h4" color="info.main">
                {statusCounts.assigned?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {statusCounts.assigned?.total_quantity || 0} items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                On Truck
              </Typography>
              <Typography variant="h4" color="primary.main">
                {statusCounts.on_truck?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {statusCounts.on_truck?.total_quantity || 0} items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Used
              </Typography>
              <Typography variant="h4" color="success.main">
                {statusCounts.used?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {statusCounts.used?.total_quantity || 0} items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Need Return
              </Typography>
              <Typography variant="h4" color="warning.main">
                {statusCounts.need_to_return?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {statusCounts.need_to_return?.total_quantity || 0} items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Records
              </Typography>
              <Typography variant="h4" color="text.primary">
                {status_summary.reduce((sum, item) => sum + parseInt(item.count), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {status_summary.reduce((sum, item) => sum + parseInt(item.total_quantity), 0)} items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Current Inventory Table */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" component="h2">
            Current Vehicle Inventory
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Items currently assigned to this technician (excluding returned items)
          </Typography>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Part Number</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Issued From</TableCell>
                <TableCell>Issued Date</TableCell>
                <TableCell>Notes</TableCell>
                {canManage && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {current_inventory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 8 : 7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No current inventory found for this technician.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                current_inventory.map((record) => (
                  <TableRow key={record.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {record.part?.internal_part_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {record.part?.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {record.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusDisplayText(record.status)}
                        color={getStatusColor(record.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {record.location?.warehouse?.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {record.location?.rack}-{record.location?.shelf}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(record.issued_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {record.notes || 'No notes'}
                      </Typography>
                    </TableCell>
                    {canManage && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Update Status">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenStatusDialog(record)}
                              disabled={record.status === 'returned'}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canReturnInventory(record.status) && (
                            <Tooltip title="Return to Warehouse">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => handleOpenReturnDialog(record)}
                              >
                                <AssignmentReturnIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={handleCloseStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Inventory Status</DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Part:</strong> {selectedRecord.part?.internal_part_number} - {selectedRecord.part?.description}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Current Status:</strong> {getStatusDisplayText(selectedRecord.status)}
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>New Status</InputLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  label="New Status"
                >
                  {getAvailableStatusTransitions(selectedRecord.status).map(status => (
                    <MenuItem key={status} value={status}>
                      {getStatusDisplayText(status)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes (Optional)"
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Add any notes about this status change..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Cancel</Button>
          <Button
            onClick={handleStatusUpdate}
            variant="contained"
            disabled={!newStatus}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={returnDialogOpen} onClose={handleCloseReturnDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Return Inventory to Warehouse</DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Part:</strong> {selectedRecord.part?.internal_part_number} - {selectedRecord.part?.description}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Current Quantity:</strong> {selectedRecord.quantity}
              </Typography>

              <TextField
                fullWidth
                type="number"
                label="Quantity to Return"
                value={returnQuantity}
                onChange={(e) => setReturnQuantity(e.target.value)}
                inputProps={{ min: 1, max: selectedRecord.quantity }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Return Notes (Optional)"
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                placeholder="Add any notes about this return..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReturnDialog}>Cancel</Button>
          <Button
            onClick={handleReturn}
            variant="contained"
            color="secondary"
            disabled={!returnQuantity || parseInt(returnQuantity) <= 0}
          >
            Return to Warehouse
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TechnicianInventorySummary;
